import type { EmscriptenDownloadMonitor } from '@php-wasm/progress';
import { phpEventStdinTransfer } from '@php-wasm/util';
import type { ListFilesOptions, RmDirOptions } from './fs-helpers';
import type { PHP } from './php';
import type { PHPRequestHandler } from './php-request-handler';
import type { PHPResponse, StreamedPHPResponse } from './php-response';
import type {
	MessageListener,
	PHPEvent,
	PHPRequest,
	PHPRunOptions,
} from './universal-php';

const _private = new WeakMap<
	PHPWorker,
	{
		requestHandler?: PHPRequestHandler;
		php?: PHP;
		monitor?: EmscriptenDownloadMonitor;
	}
>();

export type LimitedPHPApi = Pick<
	PHP,
	| 'request'
	| 'defineConstant'
	| 'mkdir'
	| 'mkdirTree'
	| 'readFileAsText'
	| 'readFileAsBuffer'
	| 'writeFile'
	| 'unlink'
	| 'mv'
	| 'cp'
	| 'rmdir'
	| 'listFiles'
	| 'isDir'
	| 'fileExists'
	| 'chdir'
	| 'run'
	| 'runStream'
	| 'onMessage'
> & {
	documentRoot: PHP['documentRoot'];
	absoluteUrl: PHP['absoluteUrl'];
	addEventListener:
		| PHP['addEventListener']
		| ((event: string, listener: (event: any) => any) => void);
	removeEventListener:
		| PHP['removeEventListener']
		| ((event: string, listener: (event: any) => any) => void);
};

export type PHPWorkerEvent = PHPEvent | { type: string };
export type PHPWorkerEventListener = (event: PHPWorkerEvent) => void;
/**
 * A PHP client that can be used to run PHP code in the browser.
 */
export class PHPWorker implements LimitedPHPApi, AsyncDisposable {
	/** @inheritDoc @php-wasm/universal!RequestHandler.absoluteUrl  */
	absoluteUrl = '';
	/** @inheritDoc @php-wasm/universal!RequestHandler.documentRoot  */
	documentRoot = '';

	private chroot: string | null = null;

	#eventListeners: Map<string, Set<PHPWorkerEventListener>> = new Map();
	#phpInstancesWithWorkerListeners = new WeakSet<PHP>();

	onMessageListeners: MessageListener[] = [];
	/** @inheritDoc */
	constructor(
		requestHandler?: PHPRequestHandler,
		monitor?: EmscriptenDownloadMonitor
	) {
		/**
		 * Workaround for TypeScript limitation.
		 * Declaring a private field using the EcmaScript syntax like this:
		 *
		 *     #php: PHP
		 *
		 * Makes that field a part of the public API of the class. This means
		 * you can no longer assign seemingly compatible objects:
		 *
		 * ```ts
		 *     class PrivateEcma {
		 *       #privateProp: string = '';
		 *       callback() { }
		 *     }
		 *     interface CompatibleInterface {
		 *       callback(): void;
		 *     }
		 *     const compatObj: CompatibleInterface = {} as any;
		 *     const tsObj: PrivateEcma = compatObj;
		 *     // Property '#privateProp' is missing in type 'CompatibleInterface' but
		 *     // required in type 'PrivateEcma'
		 * ```
		 */
		_private.set(this, {
			monitor,
		});
		if (requestHandler) {
			this.__internal_setRequestHandler(requestHandler);
		}
	}

	public __internal_setRequestHandler(requestHandler: PHPRequestHandler) {
		this.absoluteUrl = requestHandler.absoluteUrl;
		this.documentRoot = requestHandler.documentRoot;
		this.chroot = this.documentRoot;
		_private.set(this, {
			..._private.get(this),
			requestHandler,
		});
	}

	/**
	 * @internal
	 * @deprecated
	 * Do not use this method directly in the code consuming
	 * the web API. It will change or even be removed without
	 * a warning.
	 */
	protected __internal_getPHP() {
		return _private.get(this)!.php;
	}

	/**
	 * @internal
	 * @deprecated
	 * Do not use this method directly in the code consuming
	 * the web API. It will change or even be removed without
	 * a warning.
	 */
	protected __internal_getRequestHandler() {
		return this.getRequestHandler();
	}

	async setPrimaryPHP(php: PHP) {
		_private.set(this, {
			..._private.get(this)!,
			php,
		});
	}

	/** @inheritDoc @php-wasm/universal!PHPRequestHandler.pathToInternalUrl  */
	pathToInternalUrl(path: string): string {
		return this.getRequestHandler().pathToInternalUrl(path);
	}

	/** @inheritDoc @php-wasm/universal!PHPRequestHandler.internalUrlToPath  */
	internalUrlToPath(internalUrl: string): string {
		return this.getRequestHandler().internalUrlToPath(internalUrl);
	}

	/**
	 * The onDownloadProgress event listener.
	 */
	async onDownloadProgress(
		callback: (progress: CustomEvent<ProgressEvent>) => void
	): Promise<void> {
		return _private
			.get(this)!
			.monitor?.addEventListener('progress', callback as any);
	}

	/** @inheritDoc @php-wasm/universal!PHP.mv  */
	async mv(fromPath: string, toPath: string) {
		return _private.get(this)!.php!.mv(fromPath, toPath);
	}

	/** @inheritDoc @php-wasm/universal!PHP.cp  */
	async cp(fromPath: string, toPath: string) {
		return _private.get(this)!.php!.cp(fromPath, toPath);
	}

	/** @inheritDoc @php-wasm/universal!PHP.rmdir  */
	async rmdir(path: string, options?: RmDirOptions) {
		return _private.get(this)!.php!.rmdir(path, options);
	}

	/** @inheritDoc @php-wasm/universal!PHPRequestHandler.request */
	async request(request: PHPRequest): Promise<PHPResponse> {
		const requestHandler = this.getRequestHandler();
		return await requestHandler.request(request);
	}

	/**
	 * Handles a request with streaming support for large responses.
	 * Returns a StreamedPHPResponse that allows processing the response
	 * body incrementally without buffering the entire response in memory.
	 *
	 * This is useful for large file downloads (>2GB) that would otherwise
	 * exceed JavaScript's Uint8Array size limits.
	 *
	 * @param request - PHP Request data.
	 */
	async requestStreamed(request: PHPRequest): Promise<StreamedPHPResponse> {
		const requestHandler = this.getRequestHandler();
		return await requestHandler.requestStreamed(request);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.run */
	async run(request: PHPRunOptions): Promise<PHPResponse> {
		const state = _private.get(this)!;
		const primaryPhp = state.php;
		if (
			!state.requestHandler &&
			!primaryPhp?.requestHandler &&
			primaryPhp
		) {
			return await primaryPhp.run(request);
		}
		const { php, reap } = await this.acquirePHPInstance();
		try {
			return await php.run(request);
		} finally {
			reap();
		}
	}

	/**
	 * Starts a PHP request and returns its output streams before PHP exits.
	 *
	 * A pooled PHP instance remains checked out until `response.finished`
	 * settles. If the request fails before returning a response, the instance
	 * is released immediately. A non-zero PHP exit is exposed through
	 * `response.exitCode` instead of making this method reject.
	 *
	 * @param request - PHP code or script path, request metadata, and environment.
	 * @returns A streamed response whose output can be consumed incrementally.
	 * @throws When a PHP instance cannot be acquired or the request cannot start.
	 */
	async runStream(request: PHPRunOptions): Promise<StreamedPHPResponse> {
		const state = _private.get(this)!;
		const primaryPhp = state.php;
		if (
			!state.requestHandler &&
			!primaryPhp?.requestHandler &&
			primaryPhp
		) {
			return await primaryPhp.runStream(request);
		}
		const { php, reap } = await this.acquirePHPInstance();
		let response: StreamedPHPResponse;
		try {
			response = await php.runStream(request);
		} catch (error) {
			reap();
			throw error;
		}
		// The caller still owns the response streams. Keep this PHP instance
		// checked out until the process behind those streams has finished.
		void response.finished.finally(reap);
		return response;
	}

	/** @inheritDoc @php-wasm/universal!/PHP.cli */
	async cli(
		argv: string[],
		options?: { env?: Record<string, string> }
	): Promise<StreamedPHPResponse> {
		const state = _private.get(this)!;
		const primaryPhp = state.php;
		if (
			!state.requestHandler &&
			!primaryPhp?.requestHandler &&
			primaryPhp
		) {
			return await primaryPhp.cli(argv, options);
		}
		const { php, reap } = await this.acquirePHPInstance();
		let response: StreamedPHPResponse;
		try {
			response = await php.cli(argv, options);
		} catch (error) {
			reap();
			throw error;
		}
		/**
		 * Register the reap() callback to run asynchronously once
		 * the response is finished.
		 *
		 * We don't await for response.finished here. It is a
		 * `StreamedPHPResponse` instance and the caller may want
		 * to start processing the streamed data immediately.
		 */
		response.finished.finally(reap);
		return response;
	}

	/** @inheritDoc @php-wasm/universal!/PHP.chdir */
	chdir(path: string): void {
		// Remember the new chroot for all PHP instances yet to be acquired.
		this.chroot = path;
		return _private.get(this)!.php!.chdir(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.chdir */
	cwd(): string {
		return _private.get(this)!.php!.cwd();
	}

	/**
	 * @returns A PHP instance with a consistent chroot.
	 */
	private async acquirePHPInstance() {
		const { php, reap } =
			await this.getRequestHandler().instanceManager.acquirePHPInstance();
		if (this.chroot !== null) {
			php.chdir(this.chroot);
		}
		this.registerWorkerListeners(php);
		return { php, reap };
	}

	/** @inheritDoc @php-wasm/universal!/PHP.setSapiName */
	setSapiName(newName: string): void {
		_private.get(this)!.php!.setSapiName(newName);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.mkdir */
	mkdir(path: string): void {
		return _private.get(this)!.php!.mkdir(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.mkdirTree */
	mkdirTree(path: string): void {
		return _private.get(this)!.php!.mkdirTree(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.readFileAsText */
	readFileAsText(path: string): string {
		return _private.get(this)!.php!.readFileAsText(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.readFileAsBuffer */
	readFileAsBuffer(path: string): Uint8Array {
		return _private.get(this)!.php!.readFileAsBuffer(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.writeFile */
	writeFile(path: string, data: string | Uint8Array): void {
		return _private.get(this)!.php!.writeFile(path, data);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.unlink */
	unlink(path: string): void {
		return _private.get(this)!.php!.unlink(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.listFiles */
	listFiles(path: string, options?: ListFilesOptions): string[] {
		return _private.get(this)!.php!.listFiles(path, options);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.isDir */
	isDir(path: string): boolean {
		return _private.get(this)!.php!.isDir(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.isFile */
	isFile(path: string): boolean {
		return _private.get(this)!.php!.isFile(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.fileExists */
	fileExists(path: string): boolean {
		return _private.get(this)!.php!.fileExists(path);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.onMessage */
	onMessage(listener: MessageListener) {
		this.onMessageListeners.push(listener);
		return async () => {
			this.onMessageListeners = this.onMessageListeners.filter(
				(l) => l !== listener
			);
		};
	}

	/** @inheritDoc @php-wasm/universal!/PHP.defineConstant */
	defineConstant(key: string, value: string | boolean | number | null): void {
		_private.get(this)!.php!.defineConstant(key, value);
	}

	/** @inheritDoc @php-wasm/universal!/PHP.addEventListener */
	addEventListener(
		eventType: PHPWorkerEvent['type'],
		listener: PHPWorkerEventListener
	): void {
		if (!this.#eventListeners.has(eventType)) {
			this.#eventListeners.set(eventType, new Set());
		}
		this.#eventListeners.get(eventType)!.add(listener);
	}

	/**
	 * Removes an event listener for a PHP event.
	 * @param eventType - The type of event to remove the listener from.
	 * @param listener - The listener function to be removed.
	 */
	removeEventListener(
		eventType: PHPWorkerEvent['type'],
		listener: PHPWorkerEventListener
	) {
		this.#eventListeners.get(eventType)?.delete(listener);
	}

	protected dispatchEvent<EventType extends PHPWorkerEvent>(
		event: EventType
	) {
		const listeners = this.#eventListeners.get(event.type);
		if (!listeners) {
			return;
		}
		// Callbacks may mutate the Set. Dispatch the listeners that matched at entry.
		const eventListeners = [...listeners];
		const transfersStdin =
			phpEventStdinTransfer in event &&
			event[phpEventStdinTransfer] === true &&
			'stdin' in event &&
			typeof ReadableStream !== 'undefined' &&
			event.stdin instanceof ReadableStream;
		if (eventListeners.length > 1 && transfersStdin) {
			/**
			 * Split the unassigned stream branch before each transfer, then give the
			 * final listener what remains. Unread branches may buffer the full input
			 * because tee() does not coordinate backpressure.
			 */
			let remainingStdin = event.stdin as ReadableStream<Uint8Array>;
			for (let index = 0; index < eventListeners.length - 1; index++) {
				const [stdin, nextStdin] = remainingStdin.tee();
				remainingStdin = nextStdin;
				eventListeners[index]({ ...event, stdin } as EventType);
			}
			eventListeners[eventListeners.length - 1]({
				...event,
				stdin: remainingStdin,
			} as EventType);
			return;
		}
		for (const listener of eventListeners) {
			listener(event);
		}
	}

	protected registerWorkerListeners(php: PHP) {
		if (this.#phpInstancesWithWorkerListeners.has(php)) {
			return;
		}
		this.#phpInstancesWithWorkerListeners.add(php);
		php.addEventListener('*', async (event) => {
			this.dispatchEvent(event);
		});
		php.onMessage(async (message) => {
			for (const listener of this.onMessageListeners) {
				const returnData = await listener(message);
				if (returnData) {
					return returnData;
				}
			}
			return '';
		});
	}

	async [Symbol.asyncDispose]() {
		await this.getRequestHandler(false)?.[Symbol.asyncDispose]();
	}

	protected getRequestHandler(required?: true): PHPRequestHandler;
	protected getRequestHandler(required: false): PHPRequestHandler | undefined;
	protected getRequestHandler(required = true) {
		const state = _private.get(this)!;
		if (state.requestHandler) {
			return state.requestHandler;
		}
		if (state.php?.requestHandler) {
			this.__internal_setRequestHandler(state.php.requestHandler);
			return state.php.requestHandler;
		}
		if (!required) {
			return undefined;
		}
		throw new Error('PHPWorker is not connected to a request handler.');
	}
}
